import {
  ThicknessAnalysisContract,
  ThicknessCIProfilePoint,
  ThicknessMeanProfilePoint,
  ThicknessPlotBundle,
  ThicknessPlotFigureBundle
} from "../types";

export type ViewportClass = "mobile" | "tablet" | "desktop";

const CONTRACT_CACHE = new Map<string, ThicknessAnalysisContract>();

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function stableHash(value: unknown): string {
  const serialized = JSON.stringify(value) ?? "";
  let hash = 5381;
  for (let index = 0; index < serialized.length; index += 1) {
    hash = ((hash << 5) + hash) ^ serialized.charCodeAt(index);
  }
  return (hash >>> 0).toString(16);
}

function normalizeMeanProfile(value: unknown): ThicknessMeanProfilePoint[] {
  if (!Array.isArray(value)) return [];
  const points = value
    .map((entry) => {
      if (!isRecord(entry)) return null;
      const height = Number(entry.height);
      const thickness = Number(entry.thickness);
      if (!Number.isFinite(height) || !Number.isFinite(thickness)) return null;
      return {
        height,
        thickness
      };
    })
    .filter((entry): entry is ThicknessMeanProfilePoint => !!entry);

  points.sort((a, b) => a.height - b.height);
  return points;
}

function normalizeCIProfile(value: unknown): ThicknessCIProfilePoint[] {
  if (!Array.isArray(value)) return [];
  const points = value
    .map((entry) => {
      if (!isRecord(entry)) return null;
      const height = Number(entry.height);
      const ciLow = Number(entry.ci_low);
      const ciHigh = Number(entry.ci_high);
      if (!Number.isFinite(height) || !Number.isFinite(ciLow) || !Number.isFinite(ciHigh)) {
        return null;
      }
      const low = Math.min(ciLow, ciHigh);
      const high = Math.max(ciLow, ciHigh);
      return {
        height,
        ci_low: low,
        ci_high: high
      };
    })
    .filter((entry): entry is ThicknessCIProfilePoint => !!entry);

  points.sort((a, b) => a.height - b.height);
  return points;
}

function normalizeFigureBundle(value: unknown): ThicknessPlotFigureBundle | undefined {
  if (!isRecord(value)) return undefined;
  const data = Array.isArray(value.data) ? value.data : [];
  const layout = isRecord(value.layout) ? value.layout : {};
  const config = isRecord(value.config) ? value.config : {};
  return { data, layout, config };
}

function normalizeSurface3dFigure(figure: ThicknessPlotFigureBundle | undefined): ThicknessPlotFigureBundle | undefined {
  if (!figure?.data?.length) return figure;
  const hasHoverableInnerSurface = figure.data.some((trace) => {
    if (!isRecord(trace)) return false;
    if (String(trace.type ?? "") !== "surface") return false;
    return Array.isArray(trace.customdata);
  });
  if (!hasHoverableInnerSurface) return figure;

  const filtered = figure.data.filter((trace) => {
    if (!isRecord(trace)) return true;
    if (String(trace.type ?? "") !== "surface") return true;
    if (Array.isArray(trace.customdata)) return true;
    return false;
  });
  if (!filtered.length) return figure;
  return {
    ...figure,
    data: filtered
  };
}

function normalizePlotBundle(value: unknown): ThicknessPlotBundle | undefined {
  if (!isRecord(value)) return undefined;
  const overview = normalizeFigureBundle(value.overview);
  const surface3d = normalizeFigureBundle(value.surface_3d);
  const heatmap = normalizeFigureBundle(value.heatmap);

  const fallbackSurface3d = (() => {
    if (surface3d) return surface3d;
    if (!overview) return undefined;
    const traces = overview.data.filter((trace) => {
      if (!isRecord(trace)) return false;
      const type = String(trace.type ?? "");
      return type === "surface" || type === "scatter3d" || type === "mesh3d";
    });
    if (!traces.length) return undefined;

    const layout = isRecord(overview.layout) ? { ...overview.layout } : {};
    delete (layout as Record<string, unknown>).xaxis;
    delete (layout as Record<string, unknown>).xaxis2;
    delete (layout as Record<string, unknown>).yaxis;
    delete (layout as Record<string, unknown>).yaxis2;
    delete (layout as Record<string, unknown>).grid;

    return {
      data: traces,
      layout,
      config: overview.config
    };
  })();

  const fallbackHeatmap = (() => {
    if (heatmap) return heatmap;
    if (!overview) return undefined;
    const traces = overview.data.filter((trace) => {
      if (!isRecord(trace)) return false;
      const type = String(trace.type ?? "");
      if (type === "heatmap") return true;
      if (type !== "scatter" && type !== "scattergl") return false;
      const sceneRef = trace.scene;
      return sceneRef == null;
    });
    if (!traces.length) return undefined;

    const layout = isRecord(overview.layout) ? { ...overview.layout } : {};
    delete (layout as Record<string, unknown>).scene;
    delete (layout as Record<string, unknown>).scene2;
    delete (layout as Record<string, unknown>).scene3;
    return {
      data: traces,
      layout,
      config: overview.config
    };
  })();

  return {
    surface_3d: normalizeSurface3dFigure(fallbackSurface3d),
    overview,
    heatmap: fallbackHeatmap,
    slices_profile: normalizeFigureBundle(value.slices_profile),
    slices_profile_relative: normalizeFigureBundle(value.slices_profile_relative)
  };
}

function decimateMatrix(matrix: unknown[], step: number): unknown[] {
  if (!Array.isArray(matrix) || step <= 1) return matrix;
  const reducedRows = matrix
    .filter((_row, rowIndex) => rowIndex % step === 0)
    .map((row) => {
      if (!Array.isArray(row)) return row;
      return row.filter((_cell, colIndex) => colIndex % step === 0);
    });
  return reducedRows;
}

function decimateVector(vector: unknown, step: number): unknown {
  if (!Array.isArray(vector) || step <= 1) return vector;
  return vector.filter((_entry, index) => index % step === 0);
}

function decimateTraceForMobile(trace: unknown): unknown {
  if (!isRecord(trace)) return trace;
  const copy: Record<string, unknown> = { ...trace };
  const traceType = String(trace.type ?? "");

  if (traceType === "surface" && Array.isArray(trace.z)) {
    const rowCount = trace.z.length;
    const step = rowCount > 160 ? 4 : rowCount > 90 ? 3 : 2;
    copy.z = decimateMatrix(trace.z, step);
    copy.x = decimateVector(trace.x, step);
    copy.y = decimateVector(trace.y, step);
    if (Array.isArray(trace.customdata)) {
      copy.customdata = decimateMatrix(trace.customdata, step);
    }
    return copy;
  }

  if (traceType === "scatter3d") {
    const x = Array.isArray(trace.x) ? trace.x : [];
    const step = x.length > 3500 ? 5 : x.length > 1500 ? 3 : 2;
    copy.x = decimateVector(trace.x, step);
    copy.y = decimateVector(trace.y, step);
    copy.z = decimateVector(trace.z, step);
    if (Array.isArray(trace.customdata)) {
      copy.customdata = decimateVector(trace.customdata, step);
    }
    return copy;
  }

  return copy;
}

function compactTrace(trace: unknown, viewportClass: ViewportClass): unknown {
  if (!isRecord(trace) || viewportClass === "desktop") return trace;
  const factor = viewportClass === "mobile" ? 0.72 : 0.86;
  const copy: Record<string, unknown> = { ...trace };

  if (isRecord(copy.line) && typeof copy.line.width === "number") {
    copy.line = { ...copy.line, width: Math.max(1, Number(copy.line.width) * factor) };
  }
  if (isRecord(copy.marker)) {
    const marker = { ...copy.marker };
    if (typeof marker.size === "number") {
      marker.size = Math.max(2, marker.size * factor);
    }
    if (isRecord(marker.line) && typeof marker.line.width === "number") {
      marker.line = { ...marker.line, width: Math.max(0.5, Number(marker.line.width) * factor) };
    }
    copy.marker = marker;
  }

  const traceType = String(copy.type ?? "");
  if (traceType === "surface" && isRecord(copy.colorbar) && typeof copy.colorbar.len === "number") {
    copy.colorbar = {
      ...copy.colorbar,
      len: Math.min(0.6, Number(copy.colorbar.len)),
      thickness: typeof copy.colorbar.thickness === "number" ? Math.max(8, Number(copy.colorbar.thickness) * factor) : undefined
    };
  }

  return copy;
}

function compactLayout(layout: Record<string, unknown>, viewportClass: ViewportClass): Record<string, unknown> {
  if (viewportClass === "desktop") return layout;
  const factor = viewportClass === "mobile" ? 0.78 : 0.9;
  const compact: Record<string, unknown> = { ...layout };

  if (isRecord(compact.margin)) {
    compact.margin = {
      ...compact.margin,
      l: typeof compact.margin.l === "number" ? Math.round(compact.margin.l * factor) : compact.margin.l,
      r: typeof compact.margin.r === "number" ? Math.round(compact.margin.r * factor) : compact.margin.r,
      t: typeof compact.margin.t === "number" ? Math.round(compact.margin.t * factor) : compact.margin.t,
      b: typeof compact.margin.b === "number" ? Math.round(compact.margin.b * factor) : compact.margin.b
    };
  }

  if (isRecord(compact.legend)) {
    compact.legend = {
      ...compact.legend,
      font: isRecord(compact.legend.font)
        ? {
            ...compact.legend.font,
            size:
              typeof compact.legend.font.size === "number"
                ? Math.max(9, Math.round(compact.legend.font.size * factor))
                : compact.legend.font.size
          }
        : compact.legend.font
    };
  }

  if (isRecord(compact.title)) {
    compact.title = {
      ...compact.title,
      font: isRecord(compact.title.font)
        ? {
            ...compact.title.font,
            size:
              typeof compact.title.font.size === "number"
                ? Math.max(12, Math.round(compact.title.font.size * factor))
                : compact.title.font.size
          }
        : compact.title.font
    };
  }

  return compact;
}

function decimatePlotBundleForViewport(
  bundle: ThicknessPlotBundle | undefined,
  viewportClass: ViewportClass
): ThicknessPlotBundle | undefined {
  if (!bundle) return bundle;

  const apply = (figure: ThicknessPlotFigureBundle | undefined): ThicknessPlotFigureBundle | undefined => {
    if (!figure) return undefined;
    const data = figure.data.map((trace) => {
      const decimated = viewportClass === "mobile" ? decimateTraceForMobile(trace) : trace;
      return compactTrace(decimated, viewportClass);
    });
    return {
      ...figure,
      data,
      layout: compactLayout(figure.layout, viewportClass)
    };
  };

  return {
    surface_3d: apply(bundle.surface_3d),
    overview: apply(bundle.overview),
    heatmap: apply(bundle.heatmap),
    slices_profile: apply(bundle.slices_profile),
    slices_profile_relative: apply(bundle.slices_profile_relative)
  };
}

function normalizeContract(contractLike: Record<string, unknown>): ThicknessAnalysisContract | null {
  const schemaVersion = typeof contractLike.schema_version === "string" ? contractLike.schema_version : "";
  if (!schemaVersion) return null;

  const normalized: ThicknessAnalysisContract = {
    ...contractLike,
    schema_version: schemaVersion,
    mean_thickness_profile: normalizeMeanProfile(contractLike.mean_thickness_profile),
    measurement_ci_profile: normalizeCIProfile(contractLike.measurement_ci_profile),
    statistics_meta: isRecord(contractLike.statistics_meta) ? contractLike.statistics_meta : {},
    metrics: isRecord(contractLike.metrics) ? contractLike.metrics : {},
    raw_points_ref: isRecord(contractLike.raw_points_ref) ? contractLike.raw_points_ref : {},
    analyzer_meta: isRecord(contractLike.analyzer_meta) ? contractLike.analyzer_meta : {},
    plot_bundle: normalizePlotBundle(contractLike.plot_bundle)
  };
  return normalized;
}

export function extractAnalysisContract(raw: Record<string, unknown> | null): ThicknessAnalysisContract | null {
  if (!raw) return null;
  if (typeof raw.schema_version === "string") {
    return normalizeContract(raw);
  }
  if (isRecord(raw.plot_bundle)) {
    return normalizeContract({
      ...raw,
      schema_version: "thickness_analysis.v1"
    });
  }
  if (isRecord(raw.analysis_payload)) {
    const nested = raw.analysis_payload;
    if (typeof nested.schema_version === "string") {
      return normalizeContract(nested);
    }
    if (isRecord(nested.plot_bundle)) {
      return normalizeContract({
        ...nested,
        schema_version: "thickness_analysis.v1"
      });
    }
  }
  return null;
}

export function getAnalysisContractForViewport(
  raw: Record<string, unknown> | null,
  viewportClass: ViewportClass
): ThicknessAnalysisContract | null {
  const contract = extractAnalysisContract(raw);
  if (!contract) return null;

  const payloadHash = stableHash(contract);
  const cacheKey = `${contract.schema_version}:${payloadHash}:${viewportClass}`;
  const cached = CONTRACT_CACHE.get(cacheKey);
  if (cached) return cached;

  const decimatedBundle = decimatePlotBundleForViewport(contract.plot_bundle, viewportClass);
  const normalized: ThicknessAnalysisContract = {
    ...contract,
    plot_bundle: decimatedBundle
  };
  CONTRACT_CACHE.set(cacheKey, normalized);
  return normalized;
}

export function hasPlotBundle(contract: ThicknessAnalysisContract | null): boolean {
  if (!contract?.plot_bundle) return false;
  const bundle = contract.plot_bundle;
  return Boolean(
    bundle.surface_3d?.data?.length ||
      bundle.overview?.data?.length ||
      bundle.heatmap?.data?.length ||
      bundle.slices_profile?.data?.length ||
      bundle.slices_profile_relative?.data?.length
  );
}

export function viewportClassFromWidth(width: number): ViewportClass {
  if (width <= 820) return "mobile";
  if (width <= 1200) return "tablet";
  return "desktop";
}
